// `CRMExportModal` — US-148, US-149, US-150, US-151.
//
// Provider picker rendered as a centred modal. Five destinations:
//   - HubSpot / Pipedrive / Salesforce  → unavailable until a real provider
//                                          connection + delivery adapter exist.
//   - CSV                               → client-side download via
//                                          `<CSVDownload>`.
//   - Webhook / Zapier                  → unavailable until durable config,
//                                          signing, and delivery exist.

import { useState } from 'react';

import { useT } from '@barel/i18n';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Pill,
  RadioCardGroup,
  toast,
  type RadioCardOption,
} from '@barel/ui';
import { type LeadRow } from '@barel/api-client/react';

import { csvFilename, leadsToCsv, type AccountLite, type BusinessLite } from './lib/leadAdapters';

export type CrmTarget = 'hubspot' | 'pipedrive' | 'salesforce' | 'csv' | 'webhook';

export interface CRMExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: LeadRow[];
  accounts: AccountLite[];
  businesses: BusinessLite[];
  /** Optional callback after successful CRM export (closes the modal). */
  onCompleted?: () => void;
}

const TARGETS: readonly CrmTarget[] = ['hubspot', 'pipedrive', 'salesforce', 'csv', 'webhook'];

const UNAVAILABLE_TARGETS: ReadonlySet<CrmTarget> = new Set([
  'hubspot',
  'pipedrive',
  'salesforce',
  'webhook',
]);

export function CRMExportModal({
  open,
  onOpenChange,
  leads,
  accounts,
  businesses,
  onCompleted,
}: CRMExportModalProps) {
  const t = useT('leads');
  const [target, setTarget] = useState<CrmTarget>('csv');

  const close = () => onOpenChange(false);

  const single = leads.length === 1;
  const titleSubject = single
    ? (leads[0]?.prospectName ?? '')
    : t('crm.modalTitleN', { count: leads.length });

  const options: RadioCardOption<CrmTarget>[] = TARGETS.map((id) => ({
    value: id,
    title: t(`crm.targets.${id}.label`),
    description: UNAVAILABLE_TARGETS.has(id)
      ? t('crm.unavailableDescription')
      : t(`crm.targets.${id}.desc`),
    icon: <span aria-hidden="true">{TARGET_EMOJI[id]}</span>,
    disabled: UNAVAILABLE_TARGETS.has(id),
    badge: UNAVAILABLE_TARGETS.has(id) ? (
      <Pill tone="neutral" size="xs" variant="soft" uppercase>
        {t('crm.unavailableBadge')}
      </Pill>
    ) : undefined,
  }));

  const handleConfirm = () => {
    // CSV is the only enabled destination until provider delivery is real.
    if (target !== 'csv') return;
    const csv = leadsToCsv(leads, accounts, businesses);
    triggerCsvDownload(csv, csvFilename());
    toast.success({ title: t('toast.csvDownloaded') });
    onCompleted?.();
    close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" data-testid="crm-export-modal">
        <DialogHeader
          eyebrow={t('crm.eyebrow')}
          title={t('crm.modalTitle', { subject: titleSubject })}
          description={t('crm.subtitle')}
          onClose={close}
        />
        <DialogBody>
          <RadioCardGroup<CrmTarget>
            value={target}
            onValueChange={(next) => setTarget(next)}
            options={options}
            name="crm-target"
            aria-label={t('crm.providerGroupLabel')}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="md" onClick={close}>
            {t('crm.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleConfirm}
            disabled={target !== 'csv'}
            data-testid="crm-export-confirm"
          >
            {t('crm.confirmCsv')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TARGET_EMOJI: Record<CrmTarget, string> = {
  hubspot: '🟧',
  pipedrive: '🟢',
  salesforce: '☁️',
  csv: '📄',
  webhook: '🔗',
};

function triggerCsvDownload(csv: string, filename: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Defer revoke so the click had time to start.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
